import { AxiosRequestConfig } from 'axios';
import { PaginatedData, PaginationParams } from 'models/PaginatedData';
import {
  DataRoomRFI,
  DataRoomRFICategory,
  DataRoomRFIStatusCounts,
  FileTypeRFI,
  RfiActivityTab,
  ParsedSpreadsheetRFIRow,
  RfiSendReminderTrigger,
} from '../../models/DataRoomRfi';
import httpClient from '../httpClient';
import { FilePreview } from 'models/File';
import { createFileFormData } from 'uploadFileUtils';

function createRfi(dataRoomId: string, values: Partial<DataRoomRFI>) {
  return httpClient.post<DataRoomRFI>(
    `data-room-rfis/data-rooms/${dataRoomId}`,
    values,
  );
}

function patchRfi(dataRoomRfiId: string, values: Partial<DataRoomRFI>) {
  return httpClient.patch<DataRoomRFI>(
    `data-room-rfis/${dataRoomRfiId}`,
    values,
  );
}

function getRfis(dataRoomId: string, params?: PaginationParams) {
  return httpClient.get<PaginatedData<DataRoomRFI>>(
    `data-room-rfis/data-rooms/${dataRoomId}`,
    {
      params,
    },
  );
}

function getUnseenCount(dataRoomId: string) {
  return httpClient.get<{ unseenCount: number }>(
    `data-room-rfis/data-rooms/${dataRoomId}/unseen-count`,
  );
}

function ignoreDocument(
  dataRoomRfiCategoryId: string,
  dataRoomRfiFileId: string,
) {
  return httpClient.post<FileTypeRFI>(
    `data-room-rfi-categories/${dataRoomRfiCategoryId}/data-room-rfi-files/${dataRoomRfiFileId}/ignore`,
  );
}

function deleteDocument(
  dataRoomRfiCategoryId: string,
  dataRoomRfiFileId: string,
) {
  return httpClient.post<FileTypeRFI>(
    `data-room-rfi-categories/${dataRoomRfiCategoryId}/data-room-rfi-files/${dataRoomRfiFileId}/delete-from-data-room`,
  );
}

function addDocumentToDR(
  dataRoomRfiCategoryId: string,
  dataRoomRfiFileId: string,
  parentId: string | null,
) {
  return httpClient.post<FileTypeRFI>(
    `data-room-rfi-categories/${dataRoomRfiCategoryId}/add-file-to-data-room`,
    {
      parentId,
      dataRoomRfiFileId,
    },
  );
}

function moveDocumentWithinDR(
  dataRoomRfiCategoryId: string,
  dataRoomRfiFileId: string,
  newParentId: string | null,
) {
  return httpClient.post<FileTypeRFI>(
    `data-room-rfi-categories/${dataRoomRfiCategoryId}/data-room-rfi-files/${dataRoomRfiFileId}/move`,
    {
      newParentId,
    },
  );
}

function closeRfi(dataRoomRfiId: string) {
  return httpClient.post<DataRoomRFI>(`data-room-rfis/${dataRoomRfiId}/close`);
}

function reopenRfi(dataRoomRfiId: string) {
  return httpClient.post<DataRoomRFI>(`data-room-rfis/${dataRoomRfiId}/reopen`);
}

function viewRfi(dataRoomRfiId: string) {
  return httpClient.post<DataRoomRFI>(`data-room-rfis/${dataRoomRfiId}/view`);
}

function changeUploadDestinationRfi(
  dataRoomRfiId: string,
  categories: Record<string, string>,
) {
  return httpClient.post<DataRoomRFI>(
    `data-room-rfis/${dataRoomRfiId}/change-upload-destination`,
    { categories },
  );
}

function sendReminders(
  dataRoomRfiId: string,
  trigger: RfiSendReminderTrigger,
  email?: string,
) {
  return httpClient.post(`data-room-rfis/${dataRoomRfiId}/send-reminder`, {
    email,
    trigger,
  });
}

function undoSendReminders(dataRoomRfiId: string, email?: string) {
  return httpClient.post(`data-room-rfis/${dataRoomRfiId}/send-reminder-undo`, {
    email,
  });
}

function sendCategoryReminder(
  dataRoomId: string,
  categoryIds: string[],
  trigger: RfiSendReminderTrigger,
) {
  return httpClient.post(
    `data-room-rfis/data-rooms/${dataRoomId}/categories/send-reminder`,
    { categoryIds, trigger },
  );
}

function undoSendCategoryReminder(dataRoomId: string, categoryIds: string[]) {
  return httpClient.post(
    `data-room-rfis/data-rooms/${dataRoomId}/categories/send-reminder-undo`,
    { categoryIds },
  );
}

function getRfiById(dataRoomRfiId: string) {
  return httpClient.get<DataRoomRFI>(`data-room-rfis/${dataRoomRfiId}`, {
    params: { $relations: '["dataRoomRFICategories"]' },
  });
}

function getRfiCategories(dataRoomRfiId: string, params?: PaginationParams) {
  return httpClient.get<PaginatedData<DataRoomRFICategory>>(
    `data-room-rfis/${dataRoomRfiId}/categories`,
    { params },
  );
}

function exportRfiCategories(
  dataRoomRfiId: string,
  uniqueId: string,
  params?: PaginationParams,
) {
  return httpClient.post<FilePreview>(
    `data-room-rfis/${dataRoomRfiId}/categories/export`,
    { uniqueId },
    { params },
  );
}

function getRfiStatusCounts(dataRoomId: string, params?: PaginationParams) {
  return httpClient.get<DataRoomRFIStatusCounts>(
    `data-room-rfis/data-rooms/${dataRoomId}/status-counts`,
    { params },
  );
}

function markRfiActivityViewed(dataRoomRfiId: string, tab: RfiActivityTab) {
  return httpClient.post(
    `data-room-rfis/${dataRoomRfiId}/activity/mark-viewed`,
    { tab },
  );
}

function getRfiSpreadsheetTemplate(dataRoomId: string) {
  return httpClient.get<string>(
    `data-room-rfis/data-rooms/${dataRoomId}/spreadsheet-template`,
  );
}

function uploadRfiSpreadsheet(
  dataRoomId: string,
  file: File,
  fileName: string,
  config?: AxiosRequestConfig,
) {
  const formData = createFileFormData(fileName, file);

  return httpClient.post<ParsedSpreadsheetRFIRow[]>(
    `data-room-rfis/data-rooms/${dataRoomId}/spreadsheet-upload`,
    formData,
    config,
  );
}

function reassignCategories(
  dataRoomId: string,
  categoryIds: string[],
  assignTo: string,
) {
  return httpClient.post(
    `data-room-rfis/data-rooms/${dataRoomId}/categories/reassign`,
    { categoryIds, assignTo },
  );
}

function undoReassignCategories(dataRoomId: string, categoryIds: string[]) {
  return httpClient.post(
    `data-room-rfis/data-rooms/${dataRoomId}/categories/reassign-undo`,
    { categoryIds },
  );
}

function getAssignedEmails(dataRoomRfiId: string) {
  return httpClient.get<{ emails: string[] }>(
    `data-room-rfis/${dataRoomRfiId}/categories/assigned-emails`,
  );
}

export default {
  createRfi,
  patchRfi,
  getRfis,
  getUnseenCount,
  ignoreDocument,
  deleteDocument,
  addDocumentToDR,
  moveDocumentWithinDR,
  closeRfi,
  reopenRfi,
  viewRfi,
  changeUploadDestinationRfi,
  sendReminders,
  sendCategoryReminder,
  undoSendReminders,
  undoSendCategoryReminder,
  getRfiById,
  getRfiCategories,
  exportRfiCategories,
  getRfiStatusCounts,
  markRfiActivityViewed,
  getRfiSpreadsheetTemplate,
  uploadRfiSpreadsheet,
  reassignCategories,
  undoReassignCategories,
  getAssignedEmails,
};
